import {
  SET_CATEGORIES,
  SET_WORDS_CATEGORIES,
  SET_WORDS_DESCRIPTION,
  SET_LOADING_DESCRIPTION,
  SET_QUESTIONS,
  SET_QUESTIONS_DETAIL,
  SET_QUESTION_DETAIL_LOAD,
  SET_QUICK_PHRASES,
  SET_TOPIC_CATEGORIES,
  SET_TOPICS
} from '@/store/mutations.js';
import notify from '@/js/helpers/notify.js';
import apiRequest from '@/js/helpers/apiRequest.js';

export default {
  state: {
    categories: [],
    wordsByCategory: [],
    quickPhrases: [],
    wordDescriptionLoading: false,
    questions: [],
    topics: [],
    topicCategories: [],
    questionsLoad: false,
    questionDetailLoad: false
  },

  mutations: {
    [SET_CATEGORIES]: (state, { categories }) => {
      state.categories = categories;
    },

    [SET_WORDS_CATEGORIES]: (state, { categoryId, words = [] }) => {
      state.wordsByCategory.push({
        categoryId,
        words
      });
    },

    [SET_WORDS_DESCRIPTION]: (state, { description, word, categoryId }) => {
      const { words } = state.wordsByCategory.find(category => category.categoryId === categoryId);

      if (words.length) {
        const currentWords = words.filter(item => item.name === word);

        for (let item of currentWords) {
          item.description = description;
        }
      }
    },

    [SET_LOADING_DESCRIPTION]: (state, payload) => {
      state.wordDescriptionLoading = payload;
    },

    [SET_QUICK_PHRASES]: (state, { quickPhrases }) => {
      state.quickPhrases = quickPhrases;
    },

    [SET_QUESTIONS]: (state, payload) => {
      state.questions = payload;
      state.questionsLoad = true;
    },

    [SET_QUESTIONS_DETAIL]: (state, { detail }) => {
      state.questions.find(question => question.id === detail.questionId).details = detail;
      state.questionDetailLoad = false;
    },

    [SET_QUESTION_DETAIL_LOAD]: (state, payload) => {
      state.questionDetailLoad = payload;
    },

    [SET_TOPIC_CATEGORIES]: (state, { categories }) => {
      state.topicCategories = categories;
    },

    [SET_TOPICS]: (state, { topics }) => {
      state.topics = topics;
    }
  },

  actions: {
    getWordsCategories: async ({ rootState, commit }, id = '') => {
      try {
        const res = await apiRequest(
          `words/categories/${rootState.currentLangId}${id.length ? `/${id}` : ''}`,
          'GET',
          null,
          true
        );

        if (res.error) {
          notify({ text: res.error });
        }

        commit(SET_CATEGORIES, { categories: res.success });
      } catch (error) {
        notify({ text: 'Something went wrong 🤯. Please try again.' });
      }
    },

    getWordsByCategory: async ({ state, commit }, { id }) => {
      if (!state.wordsByCategory.filter(item => item.categoryId === id).length) {
        const res = await apiRequest(`words/${id}`, 'GET', null, true);

        if (res.error) {
          throw { customeError: res.error };
        }

        const words = res.success.map(word => ({
          ...word,
          description: {
            definition: '',
            pronunciation: '',
            examples: '',
            synonyms: []
          }
        }));

        commit(SET_WORDS_CATEGORIES, { categoryId: id, words });
      }
    },

    getWordDescription: async ({ state, commit }, { word, categoryId }) => {
      // Если есть описание для слова в сторе то не грузим повторно
      const { words } = state.wordsByCategory.find(category => category.categoryId === categoryId);

      if (words.length) {
        const definition = words.find(item => item.name === word).description.definition;

        if (definition.length) {
          return;
        }
      }

      commit(SET_LOADING_DESCRIPTION, true);
      const body = new FormData();
      body.append('word', word);

      const res = await apiRequest(`word`, 'POST', body, true);

      commit(SET_LOADING_DESCRIPTION, false);

      if (res.error) {
        throw { customeError: res.error };
      }

      if (res.success.results.length) {
        const { definition = '', examples = '', synonyms = [] } = res.success.results[0];

        commit(SET_WORDS_DESCRIPTION, {
          description: {
            pronunciation: res.success.pronunciation ? res.success.pronunciation.all : '',
            examples: examples && examples.length ? examples[0] : '',
            synonyms,
            definition
          },
          word,
          categoryId
        });
      }
    },

    getQuickPhrases: async ({ rootState, commit }) => {
      const res = await apiRequest(`phrases/${rootState.currentLangId}`, 'GET', null, true);

      if (res.error) {
        throw { customeError: res.error };
      }

      commit(SET_QUICK_PHRASES, { quickPhrases: res.success });
    },

    getQuestions: async ({ commit }) => {
      const res = await apiRequest('questions', 'GET', null, true);

      if (res.error) {
        throw { customeError: res.error };
      }

      commit(SET_QUESTIONS, res.success);
    },

    getQuestionsDetail: async ({ commit }, { id }) => {
      commit(SET_QUESTION_DETAIL_LOAD, true);
      const res = await apiRequest(`questions/${id}`, 'GET', null, true);

      if (res.error) {
        throw { customeError: res.error };
      }

      const { answers, audio, body, id: questionId, image, video } = res.success;

      const audioList = JSON.parse(audio);
      const videoList = JSON.parse(video);

      const detail = {
        questionId,
        body,
        answers: answers.items,
        image,
        audio: audioList.length ? videoList : null,
        video: videoList.length ? videoList : null
      };

      commit(SET_QUESTIONS_DETAIL, { detail });
    },

    setQuestionToChat: async ({ rootState }, { questionId }) => {
      const body = new FormData();

      body.append('lesson_id', rootState.lesson.currentLessonId);
      body.append('ask_id', questionId);

      const res = apiRequest('question/lesson', 'POST', body, true);

      if (res.error) {
        throw { customeError: res.error };
      }
    },

    setWordToChat: async ({ rootState }, { wordId }) => {
      const body = new FormData();

      body.append('lesson_id', rootState.lesson.currentLessonId);
      body.append('word_id', wordId);

      const res = await apiRequest('words/lesson', 'POST', body, true);

      if (res.error) {
        throw { customeError: res.error };
      }
    },

    setQuestionByWordToChat: async ({ rootState }, { wordId }) => {
      const body = new FormData();

      body.append('lesson_id', rootState.lesson.currentLessonId);
      body.append('word_id', wordId);

      const res = await apiRequest('words/image/lesson', 'POST', body, true);

      if (res.error) {
        throw { customeError: res.error };
      }
    },

    getTopics: async ({ commit }) => {
      const res = await apiRequest('gettopics', 'GET', null);

      if (res.error) {
        throw { customeError: res.error };
      }

      const categories = res.success.categories.map(({ id, parent_id, name }) => ({
        id,
        parentId: parent_id,
        name
      }));

      const topics = res.success.topics.map(({ id, category_id, content, title, created_at }) => ({
        id,
        categoryId: category_id,
        contents: JSON.parse(content).items,
        title,
        date: created_at
      }));

      commit(SET_TOPIC_CATEGORIES, { categories });
      commit(SET_TOPICS, { topics });
    }
  }
};
